import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const CAPTURE_ATTR = 'data-contract-html-pdf-capture';

const PDF_PAGE_FORMAT = 'a4' as const;

/**
 * Rasterize TinyMCE HTML for placement candidate contract client preview (not used for server PDFs).
 */
export async function htmlFragmentToA4PdfBase64(html: string): Promise<string> {
  const wrapper = document.createElement('div');
  wrapper.setAttribute(CAPTURE_ATTR, 'true');
  wrapper.style.cssText = [
    'position:fixed',
    'left:-9999px',
    'top:0',
    'width:794px',
    'padding:0',
    'margin:0',
    'box-sizing:border-box',
    'background:#ffffff',
    'opacity:1',
    'pointer-events:none',
    'overflow:visible',
    'z-index:0',
  ].join(';');
  wrapper.innerHTML = html;
  document.body.appendChild(wrapper);

  try {
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    const width = Math.max(wrapper.scrollWidth, wrapper.offsetWidth, 1);
    const height = Math.max(wrapper.scrollHeight, wrapper.offsetHeight, 1);

    const canvas = await html2canvas(wrapper, {
      scale: Math.min(2, Math.max(1, (window.devicePixelRatio || 1) * 1.25)),
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      x: 0,
      y: 0,
      onclone(clonedDoc) {
        const root = clonedDoc.querySelector(`[${CAPTURE_ATTR}]`);
        if (!(root instanceof HTMLElement)) return;
        const styleEl = clonedDoc.createElement('style');
        styleEl.textContent = `
          [${CAPTURE_ATTR}] {
            opacity: 1 !important;
            background: #ffffff !important;
          }
          [${CAPTURE_ATTR}] * {
            color: #000000 !important;
            -webkit-text-fill-color: #000000 !important;
            text-shadow: none !important;
            opacity: 1 !important;
            filter: none !important;
          }
        `;
        root.insertBefore(styleEl, root.firstChild);
        root.style.opacity = '1';
        root.style.backgroundColor = '#ffffff';
      },
    });

    if (!canvas.width || !canvas.height) {
      throw new Error('html2canvas produced an empty canvas');
    }

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      unit: 'mm',
      format: PDF_PAGE_FORMAT,
      orientation: 'portrait',
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 0;
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    const y = margin;
    const pageInnerHeight = pageHeight;

    pdf.addImage(imgData, 'PNG', margin, y, imgWidth, imgHeight);
    heightLeft -= pageInnerHeight;

    while (heightLeft > 0) {
      const position = heightLeft - imgHeight + margin;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= pageInnerHeight;
    }

    const uri = pdf.output('datauristring') as string;
    const parts = uri.split(',');
    return parts[1] ?? '';
  } finally {
    document.body.removeChild(wrapper);
  }
}
